import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { MarketingArticleSummary } from "@/hooks/useMarketingArticles";
import { useLocale, intlLocale, pickDbText } from "@/lib/locale";

export interface ArticleCardProps {
  article: MarketingArticleSummary;
  variant?: "default" | "featured";
  className?: string;
}

export function ArticleCard({ article, variant = "default", className }: ArticleCardProps) {
  const { t, i18n } = useTranslation();
  const uiLocale = useLocale();
  const title = pickDbText(article, "title", uiLocale);
  const excerpt = pickDbText(article, "excerpt", uiLocale);
  const date = new Intl.DateTimeFormat(intlLocale(uiLocale), {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(article.published_at));

  const categoryLabel = t(`marketing.resources.categories.${article.category.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}.label`);
  const href = `/ressources/${article.slug}`;

  if (variant === "featured") {
    return (
      <article className={cn("border border-rule bg-background", className)}>
        <div className="grid gap-0 lg:grid-cols-[1.2fr_1fr]">
          <Link to={href} className="block aspect-[16/10] overflow-hidden bg-muted">
            {article.hero_image_url && (
              <img
                src={article.hero_image_url}
                alt=""
                loading="eager"
                fetchPriority="high"
                width={1200}
                height={750}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
              />
            )}
          </Link>
          <div className="flex flex-col justify-between gap-6 p-7 md:p-10">
            <div>
              <span className="dateline">{categoryLabel}</span>
              <h2 className="mt-4 text-display text-3xl leading-tight md:text-4xl">
                <Link to={href} className="hover:text-accent-press">{title}</Link>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">{excerpt}</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{date}</span>
              <span aria-hidden className="text-rule">·</span>
              <span>{t("marketing.resources.readingTime", { min: article.reading_minutes })}</span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className={cn("flex flex-col bg-background", className)}>
      <Link to={href} className="block aspect-[16/10] overflow-hidden bg-muted">
        {article.hero_image_url && (
          <img
            src={article.hero_image_url}
            alt=""
            loading="lazy"
            width={800}
            height={500}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]"
          />
        )}
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <span className="dateline">{categoryLabel}</span>
        <h3 className="text-display text-lg leading-tight">
          <Link to={href} className="hover:text-accent-press">{title}</Link>
        </h3>
        <p className="text-sm leading-relaxed text-muted-foreground line-clamp-3">{excerpt}</p>
        <div className="mt-auto flex items-center gap-3 pt-2 text-xs text-muted-foreground">
          <span>{date}</span>
          <span aria-hidden className="text-rule">·</span>
          <span>{t("marketing.resources.readingTime", { min: article.reading_minutes })}</span>
        </div>
      </div>
    </article>
  );
}
